import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, SearchIcon, BuildingIcon, UserIcon, CheckIcon, AlertTriangleIcon, ChevronRightIcon, ChevronDownIcon, PackageIcon, MonitorIcon, CalendarDaysIcon, FileTextIcon, InfoIcon, LockIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { RESOURCES, USAGE_TYPES, CONTRACT_TEMPLATES, BOOKING_FILES } from '../../data/windsor';
import { findUsageType, formatTime, resourcePath, toDecimal, usageTint } from './bookingHelpers';
import type { ResourceKind } from '../../types/loisirs';
interface Props {
  onCancel: () => void;
  onSaved: () => void;
}
const WEEKDAYS = [{
  value: 0,
  fr: 'Dim',
  en: 'Sun'
}, {
  value: 1,
  fr: 'Lun',
  en: 'Mon'
}, {
  value: 2,
  fr: 'Mar',
  en: 'Tue'
}, {
  value: 3,
  fr: 'Mer',
  en: 'Wed'
}, {
  value: 4,
  fr: 'Jeu',
  en: 'Thu'
}, {
  value: 5,
  fr: 'Ven',
  en: 'Fri'
}, {
  value: 6,
  fr: 'Sam',
  en: 'Sat'
}];

/** Clients already known to the recreation department. */
const CLIENTS = [{
  id: 'cl-ahmw',
  name: 'Association Hockey Mineur de Windsor',
  kind: 'organisation' as const,
  phone: '819 845-7888'
}, {
  id: 'cl-cpaw',
  name: 'Club de patinage artistique de Windsor',
  kind: 'organisation' as const,
  phone: '819 845-2214'
}, {
  id: 'cl-wild',
  name: 'Wild de Windsor',
  kind: 'organisation' as const,
  phone: '819 845-9090'
}, {
  id: 'cl-stgab',
  name: 'École Saint-Gabriel',
  kind: 'organisation' as const,
  phone: '819 845-3311'
}, {
  id: 'cl-ville',
  name: 'Ville de Windsor',
  kind: 'organisation' as const,
  phone: '819 845-7888'
}, {
  id: 'cl-karate',
  name: 'Club de karaté Windsor',
  kind: 'organisation' as const,
  phone: '819 845-6677'
}, {
  id: 'cl-luc',
  name: 'Luc Vaillancourt',
  kind: 'citoyen' as const,
  phone: '819 566-4412'
}, {
  id: 'cl-patrick',
  name: 'Patrick Labrie',
  kind: 'citoyen' as const,
  phone: '819 566-8821'
}, {
  id: 'cl-stefan',
  name: 'Stéfan Paquin',
  kind: 'citoyen' as const,
  phone: '819 566-1179'
}];
const KIND_ICON: Record<ResourceKind, typeof BuildingIcon> = {
  plateau: BuildingIcon,
  equipement: PackageIcon,
  humain: UserIcon,
  enligne: MonitorIcon
};
export function BookingForm({
  onCancel,
  onSaved
}: Props) {
  const {
    t,
    lang
  } = useLanguage();
  const fr = lang === 'fr';
  const locale = fr ? 'fr-CA' : 'en-CA';
  const [step, setStep] = useState(1);

  // Step 1
  const [clientQuery, setClientQuery] = useState('');
  const [clientId, setClientId] = useState<string | null>(null);
  const [attentionOf, setAttentionOf] = useState('');

  // Step 2
  const [expanded, setExpanded] = useState<string[]>(['res-lemay']);
  const [resourceId, setResourceId] = useState<string | null>(null);
  const [usageId, setUsageId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('2026-10-05');
  const [endDate, setEndDate] = useState('2026-12-14');
  const [recurring, setRecurring] = useState(true);
  const [days, setDays] = useState<number[]>([1]);
  const [startTime, setStartTime] = useState('18:00');
  const [endTime, setEndTime] = useState('20:00');

  // Step 3
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [free, setFree] = useState(false);
  const [billingDate, setBillingDate] = useState('2026-11-01');
  const [note, setNote] = useState('');
  const client = CLIENTS.find(c => c.id === clientId);
  const resource = RESOURCES.find(r => r.id === resourceId);
  const usage = usageId ? findUsageType(usageId) : undefined;

  /** A resource with no explicit rule is open to both client kinds. */
  const allowsClient = (r: (typeof RESOURCES)[number]) => !client || !r.bookableBy || r.bookableBy.length === 0 || r.bookableBy.includes(client.kind);
  const allowsUsage = (usageTypeId: string) => !resource || !resource.allowedUsageTypeIds || resource.allowedUsageTypeIds.includes(usageTypeId);

  /** Usage types this resource may be booked for. */
  const availableUsageTypes = USAGE_TYPES.filter(u => allowsUsage(u.id));

  // Drop selections the resource's rules no longer permit.
  useEffect(() => {
    if (usageId && !allowsUsage(usageId)) setUsageId(null);
  }, [resourceId, usageId]);
  useEffect(() => {
    if (resource && !allowsClient(resource)) setResourceId(null);
  }, [clientId]);
  const filteredClients = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    if (!q) return CLIENTS;
    return CLIENTS.filter(c => c.name.toLowerCase().includes(q));
  }, [clientQuery]);

  /** Occurrences generated from the recurrence settings. */
  const occurrences = useMemo(() => {
    if (!startDate || !endDate) return [];
    const from = new Date(`${startDate}T12:00:00`);
    const to = new Date(`${endDate}T12:00:00`);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return [];
    if (!recurring) {
      return [{
        date: startDate,
        startTime,
        endTime
      }];
    }
    const result: {
      date: string;
      startTime: string;
      endTime: string;
    }[] = [];
    const cursor = new Date(from);
    while (cursor <= to && result.length < 120) {
      if (days.includes(cursor.getDay())) {
        result.push({
          date: `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`,
          startTime,
          endTime
        });
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return result;
  }, [startDate, endDate, recurring, days, startTime, endTime]);

  /** Existing bookings on the same resource that overlap a new occurrence. */
  const conflicts = useMemo(() => {
    if (!resourceId) return [];
    const from = toDecimal(startTime);
    const to = toDecimal(endTime);
    const dates = new Set(occurrences.map(o => o.date));
    const found: {
      date: string;
      label: string;
    }[] = [];
    BOOKING_FILES.filter(b => b.resourceId === resourceId && b.status !== 'annulee').forEach(b => {
      b.slots.forEach(slot => {
        if (!dates.has(slot.date)) return;
        if (toDecimal(slot.startTime) < to && toDecimal(slot.endTime) > from) {
          found.push({
            date: slot.date,
            label: `${b.client.name} — ${findUsageType(b.usageTypeId)?.label[lang] ?? ''}`
          });
        }
      });
    });
    return found.slice(0, 6);
  }, [resourceId, occurrences, startTime, endTime, lang]);
  const parents = RESOURCES.filter(r => !r.parentId);
  const childrenOf = (id: string) => RESOURCES.filter(r => r.parentId === id);
  const money = (value: number) => value.toLocaleString(locale, {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0
  });
  const rate = resource ? client?.kind === 'organisation' ? resource.rateOrganization ?? 0 : resource.rateCitizen ?? 0 : 0;
  const hours = Math.max(toDecimal(endTime) - toDecimal(startTime), 0);
  const estimate = free ? 0 : Math.round(rate * hours * occurrences.length);
  const longDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
  const canContinue = step === 1 ? Boolean(clientId) : step === 2 ? Boolean(resourceId && usageId && occurrences.length > 0 && hours > 0) : true;
  const steps = [{
    id: 1,
    label: t('Qui', 'Who'),
    icon: UserIcon
  }, {
    id: 2,
    label: t('Quoi et quand', 'What and when'),
    icon: CalendarDaysIcon
  }, {
    id: 3,
    label: t('Contrat et facturation', 'Contract and billing'),
    icon: FileTextIcon
  }];
  const fieldLabel = 'block text-xs font-medium text-slate-600 mb-1';
  const field = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]';
  return <div className="space-y-4 animate-in fade-in duration-200" data-id="element-2749" data-mp="5olpf28a8x5q">
      <button onClick={onCancel} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-[#4F46E5] transition-colors" data-id="element-2750" data-mp="5p3qqd9sy1ki">
        <ArrowLeftIcon className="w-4 h-4" data-id="element-2751" data-mp="q6vob3icg8bb" />
        {t('Retour aux réservations', 'Back to bookings')}
      </button>

      {/* Breadcrumb */}
      <div className="bg-white rounded-xl border border-slate-200 px-6 py-5" data-id="element-2752" data-mp="v4oqzmkfuk6u">
        <h2 className="text-xl font-bold text-[#0F172A] font-heading mb-4" data-id="element-2753" data-mp="zyjk1glxobil">
          {t('Nouvelle réservation', 'New booking')}
        </h2>
        <ol className="flex items-center gap-2 flex-wrap" data-id="element-2754" data-mp="79c91i5altgg">
          {steps.map((item, i) => {
          const Icon = item.icon;
          const done = step > item.id;
          const active = step === item.id;
          return <li key={item.id} className="flex items-center gap-2" data-id="element-2755" data-mp="g8zeqg521lfb" data-mpi={i}>
                <button onClick={() => step > item.id && setStep(item.id)} disabled={step < item.id} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-[#4F46E5] text-white' : done ? 'bg-indigo-50 text-[#4F46E5] hover:bg-indigo-100' : 'text-slate-400'}`} data-id="element-2756" data-mp="l9dbv354bayk">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${active ? 'bg-white/20' : done ? 'bg-[#4F46E5] text-white' : 'bg-slate-100'}`} data-id="element-2757" data-mp="1x0te4o1u9xmm">
                    {done ? <CheckIcon className="w-3 h-3" data-id="element-2758" data-mp="1e4bfdmokoxgv" /> : item.id}
                  </span>
                  <Icon className="w-4 h-4" data-id="element-2759" data-mp="1a26rl4kt2v25" />
                  {item.label}
                </button>
                {i < steps.length - 1 && <ChevronRightIcon className="w-4 h-4 text-slate-300" data-id="element-2760" data-mp="38gi0peb6kumw" />}
              </li>;
        })}
        </ol>
      </div>

      {/* ---------------------------------------------------------- STEP 1 */}
      {step === 1 && <section className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2761" data-mp="14u0zyum8it07">
          <h3 className="text-base font-semibold text-[#0F172A] mb-1" data-id="element-2762" data-mp="1issay85svyk4">
            {t('Pour qui réservez-vous ?', 'Who is this booking for?')}
          </h3>
          <p className="text-sm text-slate-500 mb-5" data-id="element-2763" data-mp="1duqmt8tip91s">
            {t('Cherchez un organisme reconnu ou un citoyen déjà connu du service.', 'Search a recognized organization or a citizen already known to the department.')}
          </p>

          <div className="relative mb-4" data-id="element-2764" data-mp="zuxzlnh79ayk">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-2765" data-mp="v3g0l4yqlkg8" />
            <input type="search" value={clientQuery} onChange={e => setClientQuery(e.target.value)} placeholder={t('Nom de l’organisme ou du citoyen…', 'Organization or citizen name…')} aria-label={t('Rechercher un client', 'Search a client')} className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-2766" data-mp="7dbw6vdq5go6" />
          </div>

          <ul className="space-y-2 max-h-80 overflow-y-auto pr-1" data-id="element-2767" data-mp="290ol47pga0gy">
            {filteredClients.map(item => {
          const selected = clientId === item.id;
          return <li key={item.id} data-id="element-2768" data-mp="31pm287hxdr84">
                  <button onClick={() => setClientId(item.id)} className={`w-full flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${selected ? 'border-[#4F46E5] bg-indigo-50/60 ring-1 ring-[#4F46E5]/20' : 'border-slate-200 hover:bg-slate-50'}`} data-id="element-2769" data-mp="2nsgormfvtq2u">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${item.kind === 'organisation' ? 'bg-indigo-50 text-[#4F46E5]' : 'bg-slate-100 text-slate-500'}`} data-id="element-2770" data-mp="3izpo4ddvlb5f">
                      {item.kind === 'organisation' ? <BuildingIcon className="w-4 h-4" data-id="element-2771" data-mp="30bccjsfydb2a" /> : <UserIcon className="w-4 h-4" data-id="element-2772" data-mp="2vaevjj6oin2d" />}
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-2773" data-mp="2qdenj2q9ra8m">
                      <span className="block text-sm font-medium text-slate-900 truncate" data-id="element-2774" data-mp="2martcv64ld89">
                        {item.name}
                      </span>
                      <span className="block text-xs text-slate-500" data-id="element-2775" data-mp="okgqeupglvhv">
                        {item.kind === 'organisation' ? t('Organisme', 'Organization') : t('Citoyen', 'Citizen')}{' '}
                        · {item.phone}
                      </span>
                    </span>
                    {selected && <CheckIcon className="w-4 h-4 text-[#4F46E5] shrink-0" data-id="element-2776" data-mp="2c6l3vqhls5ao" />}
                  </button>
                </li>;
        })}
            {filteredClients.length === 0 && <li className="py-8 text-center" data-id="element-2777" data-mp="1jjh54393sn7c">
                <p className="text-sm text-slate-500" data-id="element-2778" data-mp="1oh90j78yirga">
                  {t('Aucun client trouvé.', 'No client found.')}
                </p>
                <button className="mt-2 text-sm font-semibold text-[#4F46E5] hover:underline" data-id="element-2779" data-mp="1shsygd1ctz3h">
                  {t('Créer une nouvelle fiche client', 'Create a new client record')}
                </button>
              </li>}
          </ul>

          {client && <div className="mt-5 pt-5 border-t border-slate-100" data-id="element-2780" data-mp="38wklsbjn8g5x">
              <label htmlFor="bf-attention" className={fieldLabel} data-id="element-2781" data-mp="38yx4ws150wkk">
                {t('À l’attention de', 'Attention of')}
              </label>
              <input id="bf-attention" type="text" value={attentionOf} onChange={e => setAttentionOf(e.target.value)} placeholder={t('Personne responsable du dossier', 'Person responsible for the file')} className={`${field} max-w-sm`} data-id="element-2782" data-mp="32hvn1h4f9enx" />
            </div>}
        </section>}

      {/* ---------------------------------------------------------- STEP 2 */}
      {step === 2 && <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-2783" data-mp="3v5j08b3d530j">
          <div className="space-y-4" data-id="element-2784" data-mp="3v6axrrgitatg">
            <section className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-2785" data-mp="czmum7516xqf">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-2786" data-mp="cxjswpuing5v">
                {t('Ressource', 'Resource')}
              </h3>
              <ul className="max-h-80 overflow-y-auto pr-1 space-y-0.5" data-id="element-2787" data-mp="rnwmpt21ejs1">
                {parents.map(parent => {
              const kids = childrenOf(parent.id);
              const Icon = KIND_ICON[parent.kind];
              const open = expanded.includes(parent.id);
              return <li key={parent.id} data-id="element-2788" data-mp="mlp5op2codiu">
                      <button onClick={() => kids.length > 0 ? setExpanded(prev => open ? prev.filter(id => id !== parent.id) : [...prev, parent.id]) : setResourceId(parent.id)} disabled={kids.length === 0 && !allowsClient(parent)} title={kids.length === 0 && !allowsClient(parent) ? t('Non réservable pour ce type de client', 'Not bookable for this client kind') : undefined} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors ${resourceId === parent.id ? 'bg-indigo-50 text-[#4F46E5]' : kids.length === 0 && !allowsClient(parent) ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50'}`} data-id="element-2789" data-mp="8wdjpm80j5q0">
                        {kids.length > 0 ? open ? <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" data-id="element-2790" data-mp="v5u4jb83zn69" /> : <ChevronRightIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" data-id="element-2791" data-mp="q7sichfdz7qk" /> : <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" data-id="element-2792" data-mp="36snugro55woo" />}
                        <span className="text-xs font-medium truncate" data-id="element-2793" data-mp="3arwo7wj6luk8">
                          {parent.name[lang]}
                        </span>
                      </button>
                      {open && kids.length > 0 && <ul className="ml-5 space-y-0.5" data-id="element-2794" data-mp="3kln80o4d5gtl">
                          {kids.map(kid => <li key={kid.id} data-id="element-2795" data-mp="3uhgup0bsqqp3">
                              <button onClick={() => setResourceId(kid.id)} disabled={!allowsClient(kid)} title={!allowsClient(kid) ? t('Non réservable pour ce type de client', 'Not bookable for this client kind') : undefined} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors ${resourceId === kid.id ? 'bg-indigo-50 text-[#4F46E5] font-medium' : !allowsClient(kid) ? 'text-slate-400 opacity-50 cursor-not-allowed' : 'hover:bg-slate-50 text-slate-600'}`} data-id="element-2796" data-mp="3uiry2z1bxwfo">
                                <span className="w-1 h-1 rounded-full bg-slate-300 shrink-0" data-id="element-2797" data-mp="16dmau5bj9kvd" />
                                <span className="text-xs truncate" data-id="element-2798" data-mp="2mrumh4urm9s8">
                                  {kid.name[lang]}
                                </span>
                                {!allowsClient(kid) && <LockIcon className="w-3 h-3 ml-auto shrink-0" data-id="element-2799" data-mp="2u9aofgku3k88" />}
                              </button>
                            </li>)}
                        </ul>}
                    </li>;
            })}
              </ul>
              {resource && <p className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500" data-id="element-2800" data-mp="38yyokeha4cud">
                  {resource.address && <span className="block" data-id="element-2801" data-mp="3dz2yflnom9bm">{resource.address}</span>}
                  {resource.capacity && <span className="block" data-id="element-2802" data-mp="347godsn2ygag">
                      {t('Capacité : ', 'Capacity: ')}
                      {resource.capacity}
                    </span>}
                  <span className="block" data-id="element-2803" data-mp="3rv80av4pkrb7">
                    {t('Taux applicable : ', 'Applicable rate: ')}
                    {rate === 0 ? t('gratuit', 'free') : `${money(rate)} / h`}
                  </span>
                  {resource.bookableBy && resource.bookableBy.length === 1 && <span className="inline-flex items-center gap-1.5 mt-2 px-2 py-0.5 rounded-full bg-indigo-50 text-[#3730A3] text-[11px] font-semibold border border-indigo-100" data-id="element-2804" data-mp="1w6w2cv8b9dm7">
                      <LockIcon className="w-3 h-3" data-id="element-2805" data-mp="1nawvnbwrj09g" />
                      {resource.bookableBy[0] === 'organisation' ? t('Organismes seulement', 'Organizations only') : t('Citoyens seulement', 'Citizens only')}
                    </span>}
                </p>}
            </section>
          </div>

          <div className="lg:col-span-2 space-y-4" data-id="element-2806" data-mp="14eokjrh2jv68">
            <section className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-2807" data-mp="2tstod2lwg12w">
              <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap" data-id="element-2808" data-mp="2tqz47qigcczv">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider" data-id="element-2809" data-mp="2yptwrk681fx6">
                  {t('Type d’utilisation', 'Usage type')}
                </h3>
                {resource?.allowedUsageTypeIds && <p className="text-xs text-slate-500" data-id="element-2810" data-mp="3ho56gpfdh11b">
                    {t(`Restreint par « ${resource.name.fr} »`, `Restricted by “${resource.name.en}”`)}
                  </p>}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-2811" data-mp="3ro5sij4mlv97">
                {availableUsageTypes.map(item => {
              const selected = usageId === item.id;
              return <button key={item.id} onClick={() => {
                setUsageId(item.id);
                setTemplateId(item.defaultContractTemplateId ?? null);
                setFree(item.defaultPricing === 'gratuit');
              }} className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-left transition-shadow ${selected ? 'ring-2 shadow-sm' : 'hover:shadow-sm'}`} style={{
                ...usageTint(item.color),
                ...(selected ? {
                  ['--tw-ring-color' as string]: item.color
                } as React.CSSProperties : {})
              }} data-id="element-2812" data-mp="g1obho7osem">
                      <span className="mt-1 w-2 h-2 rounded-full shrink-0" style={{
                  backgroundColor: item.color
                }} data-id="element-2813" data-mp="t3xx28dnbwzo" />
                      <span className="min-w-0" data-id="element-2814" data-mp="12v27qvwagvop">
                        <span className="block text-xs font-bold leading-tight" data-id="element-2815" data-mp="12tr36tv0rjwj">
                          {item.label[lang]}
                        </span>
                        <span className="block text-[10px] text-slate-500 mt-0.5" data-id="element-2816" data-mp="1buflw1vcy8fr">
                          {item.defaultPricing === 'gratuit' ? t('gratuit', 'free') : t('tarifé', 'charged')}
                          {!item.showOnPublicCalendar && ` · ${t('interne', 'internal')}`}
                        </span>
                      </span>
                    </button>;
            })}
              </div>
            </section>

            <section className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-2817" data-mp="3g0pb4s0h2hgj">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-2818" data-mp="3b3guwwyvwnhx">
                {t('Période et récurrence', 'Period and recurrence')}
              </h3>

              <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-2819" data-mp="31d4b56yw8ycu">
                <div data-id="element-2820" data-mp="31ai212wcuasi">
                  <label htmlFor="bf-start" className={fieldLabel} data-id="element-2821" data-mp="2mufg3bu5qta">
                    {t('Date de début', 'Start date')}
                  </label>
                  <input id="bf-start" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={field} data-id="element-2822" data-mp="3tzp1q57jp6fc" />
                </div>
                <div data-id="element-2823" data-mp="34lueysvl5j27">
                  <label htmlFor="bf-end" className={fieldLabel} data-id="element-2824" data-mp="34mng9ebrzgcy">
                    {t('Date de fin', 'End date')}
                  </label>
                  <input id="bf-end" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} disabled={!recurring} className={`${field} disabled:bg-slate-50 disabled:text-slate-400`} data-id="element-2825" data-mp="14ccl0yl1496" />
                </div>
              </div>

              <div className="flex gap-2 mb-4" data-id="element-2826" data-mp="3sgf57xk98w7l">
                {[{
              id: true,
              label: t('Récurrence hebdomadaire', 'Weekly recurrence')
            }, {
              id: false,
              label: t('Une seule date', 'Single date')
            }].map(option => <button key={String(option.id)} onClick={() => setRecurring(option.id)} className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${recurring === option.id ? 'border-[#4F46E5] bg-indigo-50/60 text-[#4F46E5]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-2827" data-mp="24l8sjw8k9z8z">
                    {option.label}
                  </button>)}
              </div>

              {recurring && <div className="mb-4" data-id="element-2828" data-mp="1umjn52xuq79m">
                  <span className={fieldLabel} data-id="element-2829" data-mp="1ulhyfvnwxm9f">
                    {t('Jours de la semaine', 'Days of the week')}
                  </span>
                  <div className="flex flex-wrap gap-1.5" data-id="element-2830" data-mp="36289pt77149w">
                    {WEEKDAYS.map(day => {
                const on = days.includes(day.value);
                return <button key={day.value} onClick={() => setDays(prev => prev.includes(day.value) ? prev.filter(d => d !== day.value) : [...prev, day.value])} aria-pressed={on} className={`w-12 py-2 rounded-lg border text-xs font-semibold transition-colors ${on ? 'border-[#4F46E5] bg-[#4F46E5] text-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-2831" data-mp="28nemeacyhx74">
                          {fr ? day.fr : day.en}
                        </button>;
              })}
                  </div>
                </div>}

              <div className="grid grid-cols-2 gap-3 max-w-xs" data-id="element-2832" data-mp="ynarpujdwg1o">
                <div data-id="element-2833" data-mp="tr3kzzj5z0io">
                  <label htmlFor="bf-from" className={fieldLabel} data-id="element-2834" data-mp="tohiwff1ld7u">
                    {t('Heure de début', 'Start time')}
                  </label>
                  <input id="bf-from" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className={field} data-id="element-2835" data-mp="kqnoja0u8tnb" />
                </div>
                <div data-id="element-2836" data-mp="60q3v424am2v">
                  <label htmlFor="bf-to" className={fieldLabel} data-id="element-2837" data-mp="6081rqsrxdn1">
                    {t('Heure de fin', 'End time')}
                  </label>
                  <input id="bf-to" type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className={field} data-id="element-2838" data-mp="ztpfwk53hj7" />
                </div>
              </div>
              {hours <= 0 && <p className="flex items-center gap-1.5 text-xs text-red-600 mt-2" data-id="element-2839" data-mp="1kwzgje0wvcan">
                  <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-2840" data-mp="1kuwqitzrdj5x" />
                  {t('L’heure de fin doit suivre l’heure de début.', 'The end time must follow the start time.')}
                </p>}
            </section>

            {/* Live preview */}
            <section className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-2841" data-mp="11xvckjo888pu">
              <div className="flex items-center justify-between gap-3 mb-3" data-id="element-2842" data-mp="n98wgfqrzcxk">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider" data-id="element-2843" data-mp="nb21jqlqpp3g">
                  {t('Plages générées', 'Generated slots')}
                </h3>
                <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-[#4F46E5] text-xs font-bold tabular-nums border border-indigo-100" data-id="element-2844" data-mp="e9vltjqx0ib9">
                  {occurrences.length}
                </span>
              </div>

              {occurrences.length === 0 ? <p className="text-sm text-slate-500 py-4 text-center" data-id="element-2845" data-mp="3qsoikim9yqgp">
                  {t('Choisissez une période et au moins un jour pour voir les plages.', 'Pick a period and at least one day to see the slots.')}
                </p> : <ul className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" data-id="element-2846" data-mp="ijochuvv5wz0">
                  {occurrences.map(slot => {
              const clash = conflicts.some(c => c.date === slot.date);
              return <li key={slot.date} className={`px-2 py-1 rounded-md text-[11px] border tabular-nums ${clash ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-slate-200 bg-slate-50 text-slate-600'}`} data-id="element-2847" data-mp="sclw64cldrz7">
                        {longDate(slot.date)}
                      </li>;
            })}
                </ul>}

              {conflicts.length > 0 && <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3" data-id="element-2848" data-mp="1ooa7a0vx4ntl">
                  <p className="flex items-center gap-2 text-sm font-semibold text-amber-800 mb-1.5" data-id="element-2849" data-mp="1jqs8h3bm7ozb">
                    <AlertTriangleIcon className="w-4 h-4" data-id="element-2850" data-mp="1fqrkaqyprzf2" />
                    {t('Conflit avec des réservations existantes', 'Conflict with existing bookings')}
                  </p>
                  <ul className="space-y-1" data-id="element-2851" data-mp="111bsd7ng5nxs">
                    {conflicts.map((conflict, i) => <li key={`${conflict.date}-${i}`} className="text-xs text-amber-800" data-id="element-2852" data-mp="w1ph33r6ihrm" data-mpi={i}>
                        • {longDate(conflict.date)} — {conflict.label}
                      </li>)}
                  </ul>
                </div>}
            </section>
          </div>
        </div>}

      {/* ---------------------------------------------------------- STEP 3 */}
      {step === 3 && <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-2853" data-mp="3cofb0jfwsipl">
          <div className="lg:col-span-2 space-y-4" data-id="element-2854" data-mp="3hn8q2gaughrv">
            <section className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-2855" data-mp="3hkw8i49009fc">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-2856" data-mp="2k11m98md6tgk">
                {t('Modèle de contrat', 'Contract template')}
              </h3>
              <p className="text-xs text-slate-500 mb-3" data-id="element-2857" data-mp="2twd0zqxrw0ob">
                {t('Prérempli selon le type d’utilisation choisi.', 'Prefilled from the selected usage type.')}
              </p>
              <ul className="space-y-2" data-id="element-2858" data-mp="216wdn7p0f9yy">
                {CONTRACT_TEMPLATES.map(tpl => {
              const selected = templateId === tpl.id;
              return <li key={tpl.id} data-id="element-2859" data-mp="1y5b4lub6epw1">
                      <button onClick={() => setTemplateId(tpl.id)} className={`w-full flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${selected ? 'border-[#4F46E5] bg-indigo-50/60 ring-1 ring-[#4F46E5]/20' : 'border-slate-200 hover:bg-slate-50'}`} data-id="element-2860" data-mp="1u5shqszso4qk">
                        <FileTextIcon className={`w-4 h-4 shrink-0 ${selected ? 'text-[#4F46E5]' : 'text-slate-400'}`} data-id="element-2861" data-mp="1b8hj9alzlfc6" />
                        <span className="min-w-0 flex-1" data-id="element-2862" data-mp="1k6biatb4yn31">
                          <span className="block text-sm font-medium text-slate-900" data-id="element-2863" data-mp="30lmi3t0b30c4">
                            {tpl.name[lang]}
                          </span>
                          <span className="block text-xs text-slate-500" data-id="element-2864" data-mp="2vm9v989fpt9j">
                            {tpl.scope[lang]} ·{' '}
                            {tpl.author === 'greffe' ? t('rédigé par le greffe', 'drafted by the clerk') : t('rédigé par les loisirs', 'drafted by recreation')}
                            {tpl.eSignature && ` · ${t('signature électronique', 'e-signature')}`}
                          </span>
                        </span>
                        {selected && <CheckIcon className="w-4 h-4 text-[#4F46E5] shrink-0" data-id="element-2865" data-mp="1531ne32q16wy" />}
                      </button>
                    </li>;
            })}
              </ul>
            </section>

            <section className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-id="element-2866" data-mp="w9olwcqlcwz9">
              <button onClick={() => setFree(v => !v)} className="flex items-start gap-3 text-left w-full" data-id="element-2867" data-mp="1jziz4vetonk1">
                <span role="checkbox" aria-checked={free} className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${free ? 'bg-[#4F46E5] border-[#4F46E5] text-white' : 'bg-white border-slate-300'}`} data-id="element-2868" data-mp="7kim5wz7ua5">
                  {free && <CheckIcon className="w-3.5 h-3.5" data-id="element-2869" data-mp="iwhbq6gn0gqs" />}
                </span>
                <span data-id="element-2870" data-mp="ewgmdqs02l4y">
                  <span className="block text-sm font-medium text-slate-800" data-id="element-2871" data-mp="evxbgu0rox0f">
                    {t('Location sans frais', 'No-fee rental')}
                  </span>
                  <span className="block text-xs text-slate-500 mt-0.5" data-id="element-2872" data-mp="56dtdqkvp76m">
                    {t('Un contrat à 0 $ est tout de même produit et doit être signé.', 'A $0 contract is still issued and must be signed.')}
                  </span>
                </span>
              </button>

              <div className="max-w-xs" data-id="element-2873" data-mp="157r1n6bp7acx">
                <label htmlFor="bf-billing" className={fieldLabel} data-id="element-2874" data-mp="1a3pkyfi6626h">
                  {t('Date de facturation', 'Billing date')}
                </label>
                <input id="bf-billing" type="date" value={billingDate} onChange={e => setBillingDate(e.target.value)} disabled={free} className={`${field} disabled:bg-slate-50 disabled:text-slate-400`} data-id="element-2875" data-mp="1f5opvttk575j" />
              </div>

              <div data-id="element-2876" data-mp="1xttq4rb0a9zl">
                <label htmlFor="bf-note" className={fieldLabel} data-id="element-2877" data-mp="1xuljrwl1lxn8">
                  {t('Note pour le responsable', 'Note for the manager')}
                </label>
                <textarea id="bf-note" rows={3} value={note} onChange={e => setNote(e.target.value)} className={field} data-id="element-2878" data-mp="26v9x0p2m3tz2" />
              </div>
            </section>
          </div>

          {/* Recap */}
          <aside className="space-y-4" data-id="element-2879" data-mp="7qt49fw98rv3">
            <section className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-2880" data-mp="7ruq8fet6yrl">
              <h3 className="text-sm font-bold text-[#0F172A] mb-4" data-id="element-2881" data-mp="liziuxjreqsf">
                {t('Récapitulatif', 'Summary')}
              </h3>
              <dl className="space-y-3 text-sm" data-id="element-2882" data-mp="gov9k7n0u6o6">
                {[{
              label: t('Client', 'Client'),
              value: client?.name ?? '—'
            }, {
              label: t('Ressource', 'Resource'),
              value: resourceId ? resourcePath(resourceId, lang) : '—'
            }, {
              label: t('Type d’utilisation', 'Usage type'),
              value: usage?.label[lang] ?? '—'
            }, {
              label: t('Plages', 'Slots'),
              value: `${occurrences.length}`
            }, {
              label: t('Heures totales', 'Total hours'),
              value: `${(hours * occurrences.length).toLocaleString(locale)} h`
            }].map(row => <div key={row.label} data-id="element-2883" data-mp="17xezti70ewcx">
                    <dt className="text-xs text-slate-500" data-id="element-2884" data-mp="17wdn79aci1t3">{row.label}</dt>
                    <dd className="font-medium text-slate-800" data-id="element-2885" data-mp="1by8km2p5nv1l">{row.value}</dd>
                  </div>)}
              </dl>

              <div className="mt-4 pt-4 border-t border-slate-100 flex items-baseline justify-between" data-id="element-2886" data-mp="12z3muylf45qh">
                <span className="text-xs text-slate-500" data-id="element-2887" data-mp="ozlxoi47mqgl">
                  {t('Montant estimé', 'Estimated amount')}
                </span>
                <span className="text-xl font-bold text-[#0F172A] font-heading tabular-nums" data-id="element-2888" data-mp="t1paba2m813g">
                  {money(estimate)}
                </span>
              </div>
            </section>

            <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4" data-id="element-2889" data-mp="2ndnjxd4u6yzv">
              <p className="flex items-start gap-2 text-sm text-[#3730A3]" data-id="element-2890" data-mp="1b5v7hqvcmxht">
                <InfoIcon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-2891" data-mp="166sbsx19e3mi" />
                {t('Dès que le contrat est signé, les plages sont automatiquement publiées au calendrier.', 'As soon as the contract is signed, the slots are automatically published to the calendar.')}
              </p>
            </div>
          </aside>
        </div>}

      {/* Footer actions */}
      <div className="sticky bottom-0 flex items-center justify-between gap-3 flex-wrap bg-white/95 backdrop-blur border border-slate-200 rounded-xl px-5 py-4" data-id="element-2892" data-mp="nezfasmze0a1">
        <p className="text-xs text-slate-500" data-id="element-2893" data-mp="dhuuox8sacvc">
          {t('Étape ', 'Step ')}
          {step} {t('sur', 'of')} 3
        </p>
        <div className="flex items-center gap-2" data-id="element-2894" data-mp="2cv7aos8m47dj">
          <button onClick={() => step === 1 ? onCancel() : setStep(step - 1)} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-2895" data-mp="2csuqs9janmxu">
            {step === 1 ? t('Annuler', 'Cancel') : t('Précédent', 'Back')}
          </button>
          {step < 3 ? <button onClick={() => setStep(step + 1)} disabled={!canContinue} className="inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-2896" data-mp="2p5q85hn0r1cd">
              {t('Continuer', 'Continue')}
              <ArrowRightIcon className="w-4 h-4" data-id="element-2897" data-mp="26dnk3l81sis3" />
            </button> : <button onClick={onSaved} className="inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm" data-id="element-2898" data-mp="2be1tp7pas0s8">
              <CheckIcon className="w-4 h-4" data-id="element-2899" data-mp="3mr423fh8tk1b" />
              {t('Enregistrer la réservation', 'Save booking')}
            </button>}
        </div>
      </div>
    </div>;
}